<script setup lang="ts">
import {
  inject, onBeforeMount, onMounted, onBeforeUnmount, type Ref, ref, computed,
} from 'vue';
import {
  CdrButton, CdrContainer, CdrText,
} from '@rei/cedar';

import CategoriesComponent from './internalPages/CategoriesBeta.vue';
import CardLinksComponent from './components/CardLinksComponent.vue';
import ContactUsSingle from './internalPages/ContactUsSingle.vue';
import HelpCentersLayout from './components/HelpCentersLayout.vue';
import CUContentComponent from '../../components/contactUs/CUContentComponent.vue';

import { type IHelpCenter, IHelpCenterKeys } from './models/interfaces/HelpComponent';
import { useUserAccountData } from '../../common/composables/useUserAccountData';
import { useContentLoaded } from '../../common/composables/useContentLoaded';
import { isPageOfType, scrollToTop } from '../../common/utils';
import { sendClickAnalytics } from '../../utils';
import tags from '../../utils/globalTags';
import SearchBarWrapComponent from './components/SearchBarWrapComponent.vue';
import useGladlyHcLinks from '../../common/composables/useGladlyHcLinks';

const store: any = inject('store');
const props: IHelpCenter = store.getValues(IHelpCenterKeys);

useGladlyHcLinks();

onBeforeMount(() => scrollToTop());

const showContactSection: Ref<boolean> = ref(false);
const userInfo: Ref<any> = ref();
const showContactUsSectionOnPage: Ref<boolean> = ref(true);
const checkIfArticlePageDisplayed: Ref<boolean> = ref(false);
const isContactStandalone: Ref<boolean> = ref(false);
const isLanding: Ref<boolean> = ref(true);
const isHomePage: Ref<boolean> = ref(false);

const { isContentLoaded } = useContentLoaded();
const helpCenterClass = computed(() => (isContentLoaded.value ? 'help-center-new' : 'help-center-new--min-height'));

const syncPageFlags = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const articleRoute = url.searchParams.get('article');
  isLanding.value = !!articleRoute;
  isHomePage.value = !isPageOfType('a', 'hc', 'ctgry');
  isContactStandalone.value = articleRoute === 'contact-rei';
  showContactUsSectionOnPage.value = Boolean(props.helpCenterName) && url.pathname.includes('articles');
};

onMounted(async () => {
  syncPageFlags();
  window.addEventListener('popstate', syncPageFlags);
  window.addEventListener('hashchange', syncPageFlags);

  if (!props.helpCenterName) {
    userInfo.value = await useUserAccountData().then((res) => res);
    showContactUsSectionOnPage.value = true;
  }
});

onBeforeUnmount(() => {
  if (typeof window === 'undefined') return;
  window.removeEventListener('popstate', syncPageFlags);
  window.removeEventListener('hashchange', syncPageFlags);
});

const redirectToURL = (url: string | Location) => {
  const clickData = {
    pageName: '',
    linkName: tags.HELP_CLICK_CONTACT_US,
  };
  sendClickAnalytics(clickData);
  globalThis.location = url as Location;
};

const contactUsClick = () => {
  const clickData = {
    pageName: '',
    linkName: tags.HELP_CLICK_SHOW_CONTACT_US,
  };
  sendClickAnalytics(clickData);
  if (props.showContactUsPage) {
    redirectToURL('/help/contact');
    showContactSection.value = false;
  } else {
    showContactSection.value = true;
  }
  checkIfArticlePageDisplayed.value = globalThis !== undefined && isPageOfType('a');
};
</script>

<template>
  <MiniTrayPlugin />
  <div>
    <div
      v-if="isLanding"
      id="help-center-new"
      :class="helpCenterClass"
    >
      <ContactUsSingle v-if="isContactStandalone" />
    </div>
    <div
      v-show="!isLanding && isContentLoaded"
      id="help-center-new"
      :class="helpCenterClass"
    >
      <SearchBarWrapComponent
        v-if="!props.helpCenterName && !isContactStandalone"
        help-center-name="helpCenterName"
        :is-contact-standalone="isContactStandalone"
        :is-home-page="isHomePage"
      />
      <CardLinksComponent v-if="props.showSectionCardLinks && !props.helpCenterName && isHomePage" />
      <CdrContainer v-if="isHomePage">
        <HelpCentersLayout v-show="isContentLoaded" />
      </CdrContainer>
      <CategoriesComponent v-if="!!props.helpCenterName" />
    </div>
    <CdrContainer
      v-show="showContactUsSectionOnPage"
      class="help-center"
    >
      <div v-if="!props.showContactUsAccordion && !isLanding">
        <div
          v-if="!showContactSection"
          class="help-center-contact-us"
        >
          <CdrText
            tag="h2"
            class="help-center__accordion-header"
            data-ui="still-need-more-help"
            tabindex="0"
          >
            Need more help?
          </CdrText>
          <CdrButton
            modifier="secondary"
            @click="contactUsClick"
          >
            Contact us
          </CdrButton>
        </div>
        <CUContentComponent
          v-else
          :id="(props.selectedCategory || checkIfArticlePageDisplayed) ? 'contact-us-home-class' : 'contact-us-hc-home-class'"
          :retail-store-hours-events="props.retailStoreHoursEvents"
          :email-response-time="props.emailResponseTime"
          :help-center-numbers="props.helpCenterNumbers"
          :class-name="(props.selectedCategory || checkIfArticlePageDisplayed || isLanding) ? 'contact-us-home-class' : 'contact-us-hc-home-class'"
        />
      </div>
    </CdrContainer>
  </div>
</template>

<style lang="scss">
@import 'src/main/style/helpCenter.scss';
</style>
